import { Children, createContext, HTMLAttributes, isValidElement, ReactNode, useCallback, useContext, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import styled, { keyframes } from 'styled-components';

type Notice = { id: string; contentKey: string; children: ReactNode; role: string };
type FeedbackApi = { publish: (notice: Notice) => void; remove: (id: string) => void; notify: (message: string) => void };
const FeedbackContext = createContext<FeedbackApi | null>(null);

/** Shared editors retain their original feedback outside this live-detail scope. */
export function LiveFeedbackProvider({ children }: { children: ReactNode }) {
  const [notices, setNotices] = useState<Notice[]>([]);
  const dismissed = useRef(new Map<string, string>());
  const nextId = useRef(0);
  const publish = useCallback((notice: Notice) => {
    if (dismissed.current.get(notice.id) === notice.contentKey) return;
    dismissed.current.delete(notice.id);
    setNotices(current => [...current.filter(item => item.id !== notice.id), notice]);
  }, []);
  const remove = useCallback((id: string) => {
    dismissed.current.delete(id);
    setNotices(current => current.some(item => item.id === id) ? current.filter(item => item.id !== id) : current);
  }, []);
  const notify = useCallback((message: string) => publish({ id: `action-${++nextId.current}`, contentKey: message, children: message, role: 'alert' }), [publish]);
  const api = useMemo(() => ({ publish, remove, notify }), [publish, remove, notify]);
  const close = (contentKey: string) => {
    notices.filter(item => item.contentKey === contentKey).forEach(item => dismissed.current.set(item.id, contentKey));
    setNotices(current => current.filter(item => item.contentKey !== contentKey));
  };
  // The same failure can be rendered by both a section and its editing dialog.
  const unique = Array.from(new Map(notices.map(notice => [notice.contentKey, notice])).values());
  return <FeedbackContext.Provider value={api}>
    {children}
    {createPortal(<Stack aria-label="라이브 상세 알림">{unique.map(notice =>
      <ToastItem key={notice.contentKey} notice={notice} onDismiss={() => close(notice.contentKey)} />)}</Stack>, document.body)}
  </FeedbackContext.Provider>;
}

function ToastItem({ notice, onDismiss }: { notice: Notice; onDismiss: () => void }) {
  const dismiss = useRef(onDismiss);
  dismiss.current = onDismiss;
  useEffect(() => {
    if (notice.role === 'alert') return;
    const timer = window.setTimeout(() => dismiss.current(), 3000);
    return () => window.clearTimeout(timer);
  }, [notice.contentKey, notice.role]);
  return <NoticePanel data-live-feedback="" role={notice.role} aria-live={notice.role === 'alert' ? 'assertive' : 'polite'} aria-atomic="true">
    <Content>{notice.children}</Content><Close type="button" aria-label="알림 닫기" onClick={onDismiss}>
      <svg viewBox="0 0 20 20" width="20" height="20" fill="none" aria-hidden="true"><path d="m5 5 10 10M15 5 5 15" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" /></svg>
    </Close>
  </NoticePanel>;
}

export function useLiveNotice() {
  const context = useContext(FeedbackContext);
  return context?.notify ?? window.alert.bind(window);
}

function contentText(children: ReactNode): string {
  return Children.toArray(children).map(child => isValidElement<{ children?: ReactNode }>(child)
    ? contentText(child.props.children) : String(child)).join('');
}

export default function LiveFeedback({ children, role = 'status', fallbackAs: Fallback = 'p', ...props }:
  HTMLAttributes<HTMLElement> & { fallbackAs?: 'p' | 'div' | 'span' }) {
  const context = useContext(FeedbackContext);
  const id = useId();
  const marker = useRef<HTMLSpanElement>(null);
  const [visible, setVisible] = useState(false);
  const key = contentText(children);
  useLayoutEffect(() => {
    if (!context) return;
    const ancestors: HTMLElement[] = [];
    for (let element = marker.current?.parentElement; element; element = element.parentElement) ancestors.push(element);
    const update = () => setVisible(!ancestors.some(element => element.hidden || element.getAttribute('aria-hidden') === 'true'));
    update();
    const observer = new MutationObserver(update);
    ancestors.forEach(element => observer.observe(element, { attributes: true, attributeFilter: ['hidden', 'aria-hidden'] }));
    return () => observer.disconnect();
  }, [context]);
  useEffect(() => {
    if (!context) return;
    if (visible && key) context.publish({ id, contentKey: key, children, role });
    else context.remove(id);
  }, [context, id, key, children, role, visible]);
  useEffect(() => () => {
    // Success remains readable even when a successful save closes its source dialog.
    if (role === 'alert') context?.remove(id);
  }, [context, id, role]);

  if (!context) return <Fallback {...props} role={role}>{children}</Fallback>;
  return <span ref={marker} id={props.id} hidden>{props.id ? key : undefined}</span>;
}

const enter = keyframes`from { transform: translateY(var(--space-6)); opacity: 0; } to { transform: translateY(0); opacity: 1; }`;
const Stack = styled.div`
  position: fixed; z-index: var(--z-toast); bottom: calc(var(--space-6) + env(safe-area-inset-bottom));
  left: var(--space-4); right: var(--space-4); display: flex; flex-direction: column; align-items: center;
  gap: var(--space-2); max-height: 50dvh; overflow-y: auto; pointer-events: none;
`;
const NoticePanel = styled.div`
  box-sizing: border-box; display: flex; align-items: center; gap: var(--space-4); flex-shrink: 0;
  max-width: min(40rem, 100%); padding: var(--space-3) var(--space-4); border-radius: var(--radius-md);
  background: var(--color-bg-inverse); color: var(--color-text-on-inverse); box-shadow: var(--shadow-md);
  font: var(--font-weight-regular) var(--text-body)/var(--leading-body) var(--font-family-base);
  pointer-events: auto; animation: ${enter} var(--duration-base) var(--ease-standard) both;
  button { font: inherit; color: inherit; cursor: pointer; }
  button:focus-visible { outline: 2px solid currentColor; outline-offset: 2px; }
  @media (prefers-reduced-motion: reduce) { animation: none; }
`;
const Content = styled.div`
  min-width: 0; overflow-wrap: anywhere;
  button { border: 0; background: transparent; padding: var(--space-2); min-height: var(--control-md);
    text-decoration: underline; text-underline-offset: var(--space-1); }
`;
const Close = styled.button`
  display: grid; place-items: center; flex-shrink: 0; width: var(--control-lg); height: var(--control-lg);
  padding: 0; border: 0; background: transparent; border-radius: var(--radius-sm);
`;
